import React, { useState } from 'react';
import { Row, Col, Typography, Switch, DatePicker, Space, Badge, Divider, Alert, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { SyncOutlined, DashboardOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import RealTimeStatusSection from '../../components/manufacturing/dashboard/RealTimeStatusSection';
import KPIWidgetsGrid from '../../components/manufacturing/dashboard/KPIWidgetsGrid';
import ProductionTrendsChart from '../../components/manufacturing/dashboard/ProductionTrendsChart';
import WorkstationStatusMap from '../../components/manufacturing/dashboard/WorkstationStatusMap';
import ScheduleGanttChart from '../../components/manufacturing/dashboard/ScheduleGanttChart';
import QualityMetricsPanel from '../../components/manufacturing/dashboard/QualityMetricsPanel';
import ResourceTrackingPanel from '../../components/manufacturing/dashboard/ResourceTrackingPanel';
import AlertsPanel from '../../components/manufacturing/dashboard/AlertsPanel';
import ManufacturingDashboardAlerts from '../../components/manufacturing/dashboard/ManufacturingDashboardAlerts';
import { useDashboardAlerts } from '../../hooks/useManufacturingDashboard';

const { Title, Text } = Typography;

const ManufacturingDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [kpiPeriod, setKPIPeriod] = useState<'today' | 'week' | 'month'>('today');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Fetch alerts to show badge count
  const { data: alerts } = useDashboardAlerts(autoRefresh ? 30000 : undefined);
  const criticalAlertCount = alerts?.critical?.length || 0;

  // Update last updated time periodically
  React.useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        setLastUpdated(new Date());
      }, 30000); // Update every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      {/* Manufacturing dashboard alerts (not the Notification Platform) */}
      <ManufacturingDashboardAlerts 
        enabled={autoRefresh} 
        soundEnabled={true}
        refreshInterval={30000}
      />

      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space align="center">
            <DashboardOutlined style={{ fontSize: 32, color: '#145A54' }} />
            <div>
              <Title level={2} style={{ margin: 0 }}>
                Legacy Manufacturing Dashboard
              </Title>
              <Text type="secondary" style={{ fontSize: 14 }}>
                Real-time production monitoring and analytics — use alerts and shop-floor panels below to decide what to act on first.
              </Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space size="large">
            <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Last updated
              </Text>
              <Text style={{ fontSize: 12 }}>
                {lastUpdated.toLocaleTimeString()}
              </Text>
            </Space>
            <DatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              format="DD/MM/YYYY"
              style={{ width: 150 }}
              placeholder="Select date"
            />
            <Space direction="vertical" size={0}>
              <Text style={{ fontSize: 12 }}>Auto-refresh</Text>
              <Switch
                checked={autoRefresh}
                onChange={setAutoRefresh}
                checkedChildren={<SyncOutlined spin />}
                unCheckedChildren={<SyncOutlined />}
              />
            </Space>
          </Space>
        </Col>
      </Row>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Superseded by Manufacturing Control Tower"
        description="This legacy dashboard is retained for admin reference. Supervisors should use the Control Tower for execution, inspection, material, and delivery readiness."
        action={
          <Button type="primary" size="small" onClick={() => navigate('/mfg/control-tower')}>
            Open Control Tower
          </Button>
        }
      />

      <Divider style={{ margin: '0 0 24px 0' }} />

      {/* Real-time Status Section */}
      <RealTimeStatusSection
        date={selectedDate?.format('YYYY-MM-DD')}
        refreshInterval={autoRefresh ? 30000 : undefined}
      />

      {/* KPI Widgets Grid */}
      <KPIWidgetsGrid
        period={kpiPeriod}
        onPeriodChange={setKPIPeriod}
        refreshInterval={autoRefresh ? 60000 : undefined}
      />

      {/* Production Trends & Quality Metrics */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <ProductionTrendsChart days={7} />
        </Col>
        <Col xs={24} lg={8}>
          <QualityMetricsPanel period={kpiPeriod} />
        </Col>
      </Row>

      {/* Workstation Status Map */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <WorkstationStatusMap refreshInterval={autoRefresh ? 30000 : undefined} />
        </Col>
      </Row>

      {/* Schedule Gantt Chart */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <ScheduleGanttChart
            startDate={dayjs().startOf('week').format('YYYY-MM-DD')}
            endDate={dayjs().endOf('week').format('YYYY-MM-DD')}
          />
        </Col>
      </Row>

      {/* Resource Tracking & Alerts */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <ResourceTrackingPanel refreshInterval={autoRefresh ? 60000 : undefined} />
        </Col>
        <Col xs={24} lg={8}>
          <Badge count={criticalAlertCount} offset={[10, 10]}>
            <AlertsPanel refreshInterval={autoRefresh ? 30000 : undefined} />
          </Badge>
        </Col>
      </Row>
    </div>
  );
};

export default ManufacturingDashboardPage;
